import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { WidthObserver } from '@atlaskit/width-detector/width-observer';
import {
	CornerStyle,
	Height,
	useExtensionsState,
	useProductDependencies,
} from '@atlassian/company-hub-common';
import { useHubAutoTextColor } from '@atlassian/company-hub-common/hub-auto-text-color';
import { ELEMENT_DEFAULTS } from '@atlassian/company-hub-common/hub-brand-palette';
import { resolveCornerRadiusOverride } from '@atlassian/company-hub-common/hub-corner-radius';
import { useHubTheming } from '@atlassian/company-hub-common/hub-theming-context';
import { VERTICAL_MAX_WIDTH } from '@atlassian/company-hub-common/spotlight-layout';
import { useIsHubCornerRadiusEnabled } from '@atlassian/company-hub-common/use-is-hub-corner-radius-enabled';

import { carouselContentAnalyticSource } from '../../constants';
import { type CarouselParameters, NavigationStyle } from '../../types';
import { FrameStrip } from './frame-strip';
import { CarouselLayoutRenderer } from './layout-renderer';
import { NavigationThumbnail } from './navigation-thumbnail';

const IMAGE_PERCENTAGE = 0.575;
export type CarouselContentV2Props = {
	contentId: string;
	extensionLocalId: string;
	isCompanyHub?: boolean;
	isInViewMode?: boolean;
	parameters: CarouselParameters;
};

const styles = cssMap({
	containerStyles: {
		display: 'flex',
		position: 'relative',
		flexDirection: 'row',
		overflow: 'hidden',
		width: '100%',
		justifyContent: 'flex-start',
		scrollSnapType: 'x mandatory',
	},
});

export const isCarouselVisible = (carouselRef: React.RefObject<HTMLDivElement>): boolean => {
	if (!carouselRef.current) {
		return false;
	}
	const rect = carouselRef.current.getBoundingClientRect();
	return rect.top >= 0 && rect.bottom <= window.innerHeight;
};

export const CarouselContentV2 = ({
	extensionLocalId,
	parameters,
	contentId,
	isCompanyHub,
	isInViewMode,
}: CarouselContentV2Props): React.JSX.Element => {
	const [
		{ selectedExtensionLocalId, selectedToEditCardId, selectedToRemoveCardId },
		{ setIsInAutoFocusMode, setSelectedToEditCardId },
	] = useExtensionsState();
	const { smartCardProviderComponent } = useProductDependencies();
	const SmartCardProvider = smartCardProviderComponent || Fragment;
	const [selectedFrameIndex, setSelectedFrameIndex] = useState<number>(0);
	// Separately track the index of the hero card that should be scrolled to.
	// This should be in sync with selected frame index above, but this one
	// starts at -1 to prevent scrolling on mount.
	const [scrollToHeroFrameAtIndex, setScrollToHeroFrameAtIndex] = useState<number>(-1);
	const {
		cards: frames,
		cornerStyle,
		cornerRadius,
		isAutoplayEnabled,
		navigationStyle,
	} = parameters;
	const isCornerRadiusEnabled = useIsHubCornerRadiusEnabled();
	const theming = useHubTheming();
	// For gate-ON: resolve per-extension numeric override
	const resolvedCornerRadiusOverride = isCornerRadiusEnabled
		? resolveCornerRadiusOverride(cornerRadius, cornerStyle)
		: undefined;
	// Gate ON: derive rounded/squared from the EFFECTIVE corner radius (the local
	// override, else the inherited hub global). The hero border follows
	// `var(--hub-corner-radius)` directly (see SpotlightLayout); this flag is what
	// squares the navigation thumbnails at radius 0. Gate OFF: legacy cornerStyle
	// behaviour.
	const hasRoundedCorners = isCornerRadiusEnabled
		? (resolvedCornerRadiusOverride ?? theming?.cornerRadius) !== 0
		: cornerStyle === CornerStyle.ROUNDED || !isCompanyHub || !cornerStyle;
	const [isHovered, setIsHovered] = useState<boolean>(false);

	const [width, setWidth] = useState(VERTICAL_MAX_WIDTH + 1);

	const frameIds = frames.map((frame) => frame.cardId);
	const handleSelectFrameAtIndex = useCallback(
		(requestedIndex: number) => {
			setScrollToHeroFrameAtIndex(requestedIndex);
			setSelectedFrameIndex(requestedIndex);

			if (!isInViewMode && extensionLocalId === selectedExtensionLocalId) {
				setIsInAutoFocusMode(true);
				setSelectedToEditCardId(frameIds[requestedIndex]);
			}
		},
		[
			setScrollToHeroFrameAtIndex,
			setSelectedFrameIndex,
			frameIds,
			isInViewMode,
			setIsInAutoFocusMode,
			setSelectedToEditCardId,
			extensionLocalId,
			selectedExtensionLocalId,
		],
	);

	useEffect(() => {
		if (selectedFrameIndex > frames.length - 1) {
			handleSelectFrameAtIndex(frames.length - 1);
		}
		if (!isInViewMode && selectedToEditCardId) {
			const index = frames.findIndex((card) => card.cardId === selectedToEditCardId);
			if (index !== -1) {
				handleSelectFrameAtIndex(index);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		frames.length,
		selectedFrameIndex,
		handleSelectFrameAtIndex,
		selectedToEditCardId,
		isInViewMode,
	]);

	const carouselRef = useRef<HTMLDivElement>(null);

	// Each frame carries its own background, so the colour is derived per frame
	// inside the map below — hence a resolver rather than a value.
	// See `hub-auto-text-color`.
	const resolveTextColor = useHubAutoTextColor('carousel-extension');

	const spotlightHero = (
		<Box xcss={cx(styles.containerStyles)}>
			{frames.map(
				(
					{
						cardId,
						backgroundColor,
						layout,
						imageSrc,
						imageAltText,
						title,
						description,
						textColor,
						link,
						buttonStyle,
						buttonText,
					},
					index,
				) => {
					return (
						<CarouselLayoutRenderer
							key={cardId}
							index={index}
							frameSetLength={frames.length}
							selectedFrameIndex={scrollToHeroFrameAtIndex}
							extensionLocalId={extensionLocalId}
							testId={`carousel-spotlight-${index}--${index === selectedFrameIndex ? 'active' : 'not-active'}`}
							backgroundColor={backgroundColor}
							height={Height.MEDIUM}
							layout={layout}
							imageData={imageSrc}
							imageAltText={imageAltText}
							width={width}
							imageWidthPercentage={IMAGE_PERCENTAGE}
							title={title}
							description={description}
							textColor={resolveTextColor({
								backgroundColor,
								textColor,
								defaultRef: ELEMENT_DEFAULTS['carousel.bg'],
							})}
							link={link}
							buttonStyle={buttonStyle}
							buttonText={buttonText}
							contentId={contentId}
							hasRoundedCorners={hasRoundedCorners}
							cornerRadiusOverride={resolvedCornerRadiusOverride}
							source={carouselContentAnalyticSource}
						/>
					);
				},
			)}
			<WidthObserver setWidth={setWidth} offscreen />
		</Box>
	);

	const handleMouseOrFocusChange = (event: 'enter' | 'leave') => {
		if (event === 'enter') {
			setIsHovered(true);
		} else {
			setIsHovered(false);
		}
	};

	const content = (
		<Box
			onMouseEnter={() => handleMouseOrFocusChange('enter')}
			onMouseLeave={() => handleMouseOrFocusChange('leave')}
			onFocusCapture={() => handleMouseOrFocusChange('enter')}
			onBlurCapture={() => handleMouseOrFocusChange('leave')}
			ref={carouselRef}
		>
			<Flex wrap="wrap" gap="space.150" testId="carousel-v2-content-container">
				{spotlightHero}
				<FrameStrip
					isInViewMode={!!isInViewMode}
					frameIds={frames.map((frame) => frame.cardId)}
					selectedHeroFrameIndex={selectedFrameIndex}
					setSelectedHeroFrameIndex={handleSelectFrameAtIndex}
					extensionLocalId={extensionLocalId}
					isHovered={isHovered}
					isCarouselVisible={() => isCarouselVisible(carouselRef)}
					isAutoplayEnabled={isAutoplayEnabled && isInViewMode}
					navigationStyle={navigationStyle || NavigationStyle.THUMBNAILS}
					width={width}
					frames={frames.map((frame, index) => {
						const isSelectedToEdit =
							selectedToEditCardId === frame.cardId &&
							extensionLocalId === selectedExtensionLocalId;
						const isSelectedToRemove =
							selectedToRemoveCardId === frame.cardId &&
							extensionLocalId === selectedExtensionLocalId;

						return (
							<NavigationThumbnail
								contentId={contentId}
								wrapperHeight={40}
								minWidth={200}
								maxWidth={200}
								onSucceeded={() => {}}
								onFailed={() => {}}
								isEmpty={!frame.link}
								isSelected={selectedFrameIndex === index}
								hasRoundedCorners={hasRoundedCorners}
								{...frame}
								setSelectedHeroFrameIndex={handleSelectFrameAtIndex}
								index={index}
								isSelectedToEdit={isSelectedToEdit}
								isSelectedToRemove={isSelectedToRemove}
							/>
						);
					})}
				/>
			</Flex>
		</Box>
	);

	return <SmartCardProvider>{content}</SmartCardProvider>;
};
